import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertTriangle, BookOpen, Users, RefreshCw, Loader2,
  Mail, Check, ChevronDown, ChevronUp, TrendingDown, Bell,
  Brain, Sparkles, Copy, X, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { streamLessonNote } from '../../api';

const SEVERITY_STYLE = {
  high:   { bg: 'bg-rose-50',   border: 'border-rose-200',   badge: 'bg-rose-100 text-rose-700',   icon: 'text-rose-500' },
  medium: { bg: 'bg-amber-50',  border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-700', icon: 'text-amber-500' },
};

/* ── Lesson Plan Modal ────────────────────────────────────────────────── */
function LessonPlanModal({ alert, mySubjects, onClose }) {
  const [subject, setSubject] = useState(mySubjects.length === 1 ? mySubjects[0] : '');
  const [duration, setDuration] = useState(45);
  const [extraNotes, setExtraNotes] = useState('');
  const [output, setOutput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const outputRef = useRef(null);

  const handleGenerate = async () => {
    if (!subject) { toast.error('Please select a subject'); return; }
    setGenerating(true);
    setOutput('');
    setDone(false);

    try {
      const contextNote = `Remediation focus: ${alert.student_count} student${alert.student_count !== 1 ? 's' : ''} in ${alert.class_name} are struggling with this topic.${extraNotes ? '\nAdditional context: ' + extraNotes : ''}`;
      const response = await streamLessonNote({
        subject,
        class_level: alert.class_name,
        topic:       alert.topic,
        duration,
        extra_notes: contextNote,
      });

      if (!response.ok) { toast.error('AI generation failed — try again'); setGenerating(false); return; }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';

      while (true) {
        const { done: rdDone, value } = await reader.read();
        if (rdDone) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.token) {
              acc += data.token;
              setOutput(acc);
              outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight, behavior: 'smooth' });
            }
            if (data.done) { setDone(true); }
          } catch {}
        }
      }
    } catch (e) {
      toast.error('Failed to generate lesson plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh', border: '1px solid #E2E8F0', boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#eef1ff] flex items-center justify-center flex-shrink-0">
              <Brain className="w-5 h-5 text-[#2563EB]" strokeWidth={1.75} />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Generate Remediation Lesson Plan
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                <span className="font-semibold text-slate-700">{alert.topic}</span>
                {' · '}{alert.class_name}
                {' · '}{alert.student_count} student{alert.student_count !== 1 ? 's' : ''} struggling
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Config */}
        {!done && !generating && output === '' && (
          <div className="p-6 space-y-4 border-b border-slate-100">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Subject *</label>
                {mySubjects.length > 0 ? (
                  <select
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400"
                    data-testid="lesson-plan-subject-select"
                  >
                    <option value="">Select subject</option>
                    {mySubjects.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="e.g. Mathematics"
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400"
                    data-testid="lesson-plan-subject-input"
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Duration (mins)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(Number(e.target.value))}
                  min={20} max={120}
                  className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400"
                  data-testid="lesson-plan-duration-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Additional Notes (optional)</label>
              <textarea
                value={extraNotes}
                onChange={e => setExtraNotes(e.target.value)}
                placeholder="e.g. Focus on visual explanations, students struggle with fractions..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 resize-none"
                data-testid="lesson-plan-notes-input"
              />
            </div>
          </div>
        )}

        {/* Output */}
        {(generating || output) && (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div
              ref={outputRef}
              className="flex-1 overflow-y-auto p-6 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap font-mono"
              style={{ minHeight: '200px', maxHeight: '420px', backgroundColor: '#F8FAFC' }}
              data-testid="lesson-plan-output"
            >
              {output}
              {generating && (
                <span className="inline-block w-0.5 h-4 bg-blue-500 animate-pulse ml-0.5 align-text-bottom" />
              )}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="p-5 border-t border-slate-100 flex gap-3 justify-between items-center">
          <div className="flex items-center gap-2">
            {done && (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
                <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Plan generated
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {done && output && (
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="copy-lesson-plan-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            {(done || output) && (
              <button
                onClick={() => { setOutput(''); setDone(false); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="regenerate-lesson-plan-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate
              </button>
            )}
            {!done && !generating && (
              <button
                onClick={handleGenerate}
                disabled={!subject}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-colors disabled:opacity-40"
                style={{ backgroundColor: '#2563EB' }}
                data-testid="generate-lesson-plan-btn"
              >
                <Wand2 className="w-3.5 h-3.5" /> Generate Plan
              </button>
            )}
            {generating && (
              <button disabled className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white opacity-80" style={{ backgroundColor: '#2563EB' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Alert Card ────────────────────────────────────────────────────────── */
function AlertCard({ alert, idx, mySubjects, onLessonPlan }) {
  const s = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.medium;
  const [open, setOpen] = useState(false);
  const students = alert.students || [];

  return (
    <div className={`rounded-2xl border ${s.border} ${s.bg} overflow-hidden`}
      data-testid={`weak-topic-alert-${idx}`}>
      {/* Main row */}
      <div className="p-4 flex items-start gap-4">
        <div className={`w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border ${s.border}`}>
          <AlertTriangle className={`w-[18px] h-[18px] ${s.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="font-bold text-slate-800 text-sm">{alert.topic}</p>
              <p className="text-xs text-slate-500 mt-0.5">Class: <strong>{alert.class_name}</strong></p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${s.badge} flex-shrink-0`}>
              {alert.student_count} students struggling
            </span>
          </div>
          <p className="text-xs text-slate-600 mt-2">
            {alert.severity === 'high'
              ? 'Urgent: Many students are struggling. Consider revising this topic.'
              : 'Multiple students need more practice on this topic.'}
          </p>

          {/* Action buttons */}
          <div className="flex gap-2 mt-3 flex-wrap">
            {/* AI Lesson Plan button */}
            <button
              onClick={() => onLessonPlan(alert)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors"
              data-testid={`generate-lesson-plan-${idx}`}
            >
              <Brain className="w-3 h-3" />
              Generate Lesson Plan
            </button>

            {/* See who button */}
            {students.length > 0 && (
              <button
                onClick={() => setOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${open ? 'bg-white border-slate-200 text-slate-700' : 'bg-white/70 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'}`}
                data-testid={`alert-drilldown-btn-${idx}`}
              >
                <Users className="w-3 h-3" />
                {open ? 'Hide' : 'See who'}
                {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Student list drill-down */}
      {open && students.length > 0 && (
        <div className="border-t border-current/10 bg-white/50 px-4 py-3" data-testid={`alert-students-${idx}`}>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
            Struggling Students ({students.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {students.map((name, i) => (
              <span key={i} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm">
                <div className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-bold text-slate-500 flex-shrink-0">
                  {name?.[0]?.toUpperCase() || '?'}
                </div>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────────────── */
export default function TeacherWeakTopicAlertsPage() {
  const [alerts, setAlerts]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [mySubjects, setMySubjects]   = useState([]);
  const [lessonPlanAlert, setLessonPlanAlert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/growth/teacher/weak-topic-alerts');
      setAlerts((r.data && r.data.alerts) || r.alerts || []);
    } catch { toast.error('Failed to load alerts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get('/academics/my-assignments').then(r => {
      const subjects = [...new Set((r.data || []).map(a => a.subject_name).filter(Boolean))].sort();
      setMySubjects(subjects);
    }).catch(() => {});
  }, [load]);

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const r = await api.post('/growth/teacher/send-weak-topic-email');
      toast.success(`Email sent: ${r.sent || 0} delivered`);
    } catch { toast.error('Failed to send email'); }
    finally { setSending(false); }
  };

  const highCount   = alerts.filter(a => a.severity === 'high').length;
  const mediumCount = alerts.filter(a => a.severity === 'medium').length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="weak-topic-alerts-page">
      {/* Lesson Plan Modal */}
      {lessonPlanAlert && (
        <LessonPlanModal
          alert={lessonPlanAlert}
          mySubjects={mySubjects}
          onClose={() => setLessonPlanAlert(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">Weak Topic Alerts</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {loading ? 'Loading…' : `${alerts.length} alert${alerts.length !== 1 ? 's' : ''} across your classes`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40"
            data-testid="refresh-alerts-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleSendEmail}
            disabled={sending || alerts.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-40"
            data-testid="send-email-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Email Alerts
          </button>
        </div>
      </div>

      {/* Summary pills */}
      {alerts.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            <Bell className="w-3.5 h-3.5 text-rose-500" />
            <span className="text-xs font-bold text-rose-700">{highCount} High priority</span>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <TrendingDown className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-bold text-amber-700">{mediumCount} Medium priority</span>
          </div>
        </div>
      )}

      {/* Alerts */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 py-20 text-center">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-emerald-500" />
          </div>
          <p className="text-slate-800 font-semibold">No weak topic alerts</p>
          <p className="text-slate-500 text-sm mt-1">All topics are performing well across your classes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert, idx) => (
            <AlertCard
              key={idx}
              alert={alert}
              idx={idx}
              mySubjects={mySubjects}
              onLessonPlan={setLessonPlanAlert}
            />
          ))}
        </div>
      )}
    </div>
  );
}
